import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface AppTheme {
  id: string;
  name: string;
  description: string;
  previewGradient: string;
  accentColor: string;
  isDark?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  public static readonly STORAGE_KEY = 'healins_app_theme';

  public readonly themes: AppTheme[] = [
    {
      id: 'ms-office',
      name: 'Microsoft Office Professional',
      description: 'Executive blue accent, crisp Segoe UI typography (Default)',
      previewGradient: 'linear-gradient(135deg, #0078d4 0%, #005a9e 100%)',
      accentColor: '#0078d4'
    },
    {
      id: 'light-modern',
      name: 'Light Modern',
      description: 'Vibrant indigo & violet hues with soft pastel shadows',
      previewGradient: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
      accentColor: '#6366f1'
    },
    {
      id: 'dark-sleek',
      name: 'Sleek Dark',
      description: 'Deep obsidian backdrop with glowing emerald & cyan accents',
      previewGradient: 'linear-gradient(135deg, #0f172a 0%, #06b6d4 100%)',
      accentColor: '#06b6d4',
      isDark: true
    },
    {
      id: 'tesla-style',
      name: 'Tesla Minimalist',
      description: 'Monochrome contrast with signature crimson red accent',
      previewGradient: 'linear-gradient(135deg, #111111 0%, #e82127 100%)',
      accentColor: '#e82127',
      isDark: true
    },
    {
      id: 'japanese-zen',
      name: 'Japanese Zen',
      description: 'Serene warm parchment, matcha green & sakura tones',
      previewGradient: 'linear-gradient(135deg, #2d5a27 0%, #d87d8a 100%)',
      accentColor: '#2d5a27'
    },
    {
      id: 'euro-tropical',
      name: 'European Tropical',
      description: 'French Riviera palms, sunlit terracotta & warm Mediterranean teal',
      previewGradient: 'linear-gradient(135deg, #0d9488 0%, #d97706 100%)',
      accentColor: '#0d9488'
    },
    {
      id: 'oceanic-deep',
      name: 'Oceanic Sapphire',
      description: 'Deep Pacific marine blue, aquamarine wave glows & pearl tones',
      previewGradient: 'linear-gradient(135deg, #0284c7 0%, #06b6d4 100%)',
      accentColor: '#0284c7'
    },
    {
      id: 'himalayan-peak',
      name: 'Himalayan Mountains',
      description: 'Majestic glacial peaks, crisp alpine snow, ice blue & pine slate gradients',
      previewGradient: 'linear-gradient(135deg, #0284c7 0%, #38bdf8 50%, #e2e8f0 100%)',
      accentColor: '#0284c7'
    },
    {
      id: 'haridwar-divine',
      name: 'Holy City Haridwar',
      description: 'Sacred Ganges Aarti gold, marigold saffron, diyas & spiritual temple amber',
      previewGradient: 'linear-gradient(135deg, #c2410c 0%, #ea580c 50%, #eab308 100%)',
      accentColor: '#ea580c'
    }
  ];

  private currentThemeSubject = new BehaviorSubject<string>('ms-office');
  public currentTheme$: Observable<string> = this.currentThemeSubject.asObservable();

  constructor() {
    const savedTheme = localStorage.getItem(ThemeService.STORAGE_KEY) || 'ms-office';
    this.setTheme(savedTheme);
  }

  public get currentTheme(): string {
    return this.currentThemeSubject.value;
  }

  public setTheme(themeId: string): void {
    const theme = this.themes.find(t => t.id === themeId) || this.themes[0];
    const targetThemeId = theme.id;

    // Apply data-theme attribute on document root and body
    document.documentElement.setAttribute('data-theme', targetThemeId);
    document.body.setAttribute('data-theme', targetThemeId);

    // Remove previous theme classes from body
    this.themes.forEach(t => {
      document.body.classList.remove(`theme-${t.id}`);
    });
    document.body.classList.add(`theme-${targetThemeId}`);

    // Persist choice in LocalStorage
    localStorage.setItem(ThemeService.STORAGE_KEY, targetThemeId);
    this.currentThemeSubject.next(targetThemeId);
  }
}
